import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { getBlog, getWriting } from "@/lib/api";

/**
 * RelatedPosts — surfaces 3 articles that share the most tags/category with
 * the current post so readers stay one more click deep in the blog.
 *
 * Scoring:
 *   +3 for matching category
 *   +2 for each shared tag
 * The current post is always excluded. When nothing scores > 0 we fall back
 * to the most recent 3 posts from the same collection so the section is
 * never empty (except when there are literally no other posts at all).
 */
export default function RelatedPosts({ post, source = "blog", className = "" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!post) return;
    const loader = source === "writing" ? getWriting : getBlog;
    loader()
      .then((all = []) => {
        const others = all.filter((p) => (p.id || p.slug) !== (post.id || post.slug));
        const wantTags = (post.tags || []).map((t) => (t || "").toLowerCase());
        const wantCat = (post.category || "").toLowerCase();
        const scored = others.map((p) => {
          let score = 0;
          const pTags = (p.tags || []).map((t) => (t || "").toLowerCase());
          const pCat = (p.category || "").toLowerCase();
          if (wantCat && pCat === wantCat) score += 3;
          wantTags.forEach((t) => { if (t && pTags.includes(t)) score += 2; });
          return { p, score };
        });
        const contextual = scored
          .filter((x) => x.score > 0)
          .sort((a, b) => b.score - a.score || ((b.p.date || "").localeCompare(a.p.date || "")))
          .map((x) => x.p);
        const fallback = others.slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        const finalList = (contextual.length ? contextual : fallback).slice(0, 3);
        setItems(finalList);
      })
      .catch(() => setItems([]));
  }, [post, source]);

  if (!items.length) return null;

  const linkBase = source === "writing" ? "/writing" : "/blog";

  return (
    <section data-testid="related-posts" className={`border-t border-border pt-14 ${className}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="overline text-primary mb-2">Keep reading</p>
          <h3 className="font-display text-3xl md:text-4xl">Related articles</h3>
        </div>
        <Link
          to={linkBase}
          data-testid="related-posts-all"
          className="font-mono text-xs uppercase tracking-widest text-primary link-underline"
        >
          All articles →
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((p) => (
          <Link
            key={p.id || p.slug}
            to={`${linkBase}/${p.slug || p.id}`}
            data-testid={`related-post-${p.slug || p.id}`}
            className="group flex flex-col border border-border bg-background hover-lift"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
              {p.image ? (
                <img src={p.image} alt={p.title}
                     className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-secondary to-background" />
              )}
              {p.category && (
                <span className="absolute top-3 left-3 font-mono text-[10px] bg-primary text-white px-2 py-1 uppercase tracking-widest">
                  {p.category}
                </span>
              )}
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <h4 className="font-display text-xl leading-snug line-clamp-3 group-hover:text-primary transition-colors">
                {p.title}
              </h4>
              {p.excerpt && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
              )}
              <div className="mt-4 flex items-center justify-between font-mono text-[11px] text-muted-foreground">
                <span>{p.date}</span>
                <span className="inline-flex items-center gap-1 text-primary">
                  Read <ArrowUpRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
